import React, { useState } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Download, Upload, Database, FileText, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBackupExport = () => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDatabaseBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup`);
      
      // Create and download file
      const blob = new Blob([response.data.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Backup u krijua me sukses! Madhësia: ${(response.data.size / 1024).toFixed(2)} KB`);
    } catch (error) {
      toast.error('Gabim në krijimin e backup');
      console.error('Backup error:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleClientsExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/admin/clients/export`);
      
      // Decode base64 and create Excel file
      const binaryString = atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Eksporti përfundoi! ${response.data.count} klientë u eksportuan.`);
    } catch (error) {
      toast.error('Gabim në eksportimin e klientëve');
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleClientsImport = async () => {
    if (!selectedFile) {
      toast.error('Zgjidhni një skedar Excel');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setImportLoading(true);
    try {
      const response = await axios.post(`${API}/admin/clients/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(
        `Import përfundoi! ${response.data.imported} klientë të importuar, ${response.data.skipped} të anashkaluar.`
      );
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Import errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} gabime gjatë importit. Kontrolloni konsolën.`);
      }
      
      setSelectedFile(null);
    } catch (error) {
      toast.error('Gabim në importimin e klientëve');
      console.error('Import error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
    } else {
      toast.error('Vetëm skedarë Excel (.xlsx, .xls) janë të lejuar');
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Database Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Backup i Bazës së të Dhënave
          </CardTitle>
          <CardDescription>
            Krijoni një backup të plotë të bazës së të dhënave me të gjitha koleksionet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Informacion për Backup</h4>
                <p className="text-sm text-blue-800 mt-1">
                  Backup përfshin: klientët, tiketat, mesazhet e chat-it, audit logs, përdoruesit, 
                  dhe të gjitha konfiguracionet e sistemit.
                </p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleDatabaseBackup}
            disabled={backupLoading}
            className="w-full"
          >
            {backupLoading ? (
              'Duke krijuar backup...'
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Krijo Backup të DB
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Clients Export/Import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Menaxhimi i Klientëve në Excel
          </CardTitle>
          <CardDescription>
            Eksportoni klientët në Excel ose importoni klientë të rinj nga Excel
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Export Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-900">Export Klientët</h4>
            <p className="text-sm text-slate-600">
              Eksportoni të gjithë klientët në një skedar Excel me të gjitha të dhënat.
            </p>
            <Button
              onClick={handleClientsExport}
              disabled={exportLoading}
              variant="outline"
              className="w-full"
            >
              {exportLoading ? (
                'Duke eksportuar...'
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Eksporto Klientët në Excel
                </>
              )}
            </Button>
          </div>

          <div className="border-t pt-6">
            {/* Import Section */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Import Klientët</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h5 className="font-medium text-yellow-900">Kërkesa për Formatin</h5>
                    <p className="text-sm text-yellow-800 mt-1">
                      Excel duhet të ketë kolonat: <strong>Emri</strong> (e detyrueshme), 
                      <strong>Telefoni</strong> (e detyrueshme), Email, Adresa, Shenim Teknik
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="excel-file">Zgjidhni skedarin Excel</Label>
                <Input
                  id="excel-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="mt-1"
                />
                {selectedFile && (
                  <p className="text-sm text-green-600 mt-2">
                    Skedari i zgjedhur: {selectedFile.name}
                  </p>
                )}
              </div>
              
              <Button
                onClick={handleClientsImport}
                disabled={importLoading || !selectedFile}
                className="w-full"
              >
                {importLoading ? (
                  'Duke importuar...'
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importo Klientët nga Excel
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackupExport;